import { AGENCIES } from './agencies-data';

export const SAP_NUMBER = 'SAP991703604';
export const WHATSAPP_NUMBER = '0649074417';
export const WHATSAPP_INTL = '33649074417';

export const slugify = (str) =>
  str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/œ/g, 'oe')
    .replace(/Œ/g, 'oe')
    .toLowerCase()
    .replace(/['’]/g, '-')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

// Editorial metadata for the main cities (postal code, population hint, local intro)
const CITY_META = {
  'Arras': { cp: '62000', intro: "Préfecture du Pas-de-Calais, Arras et sa couronne (Saint-Laurent-Blangy, Achicourt, Dainville, Beaurains) comptent de nombreux actifs et retraités qui souhaitent déléguer l'entretien du domicile. Nos Easers interviennent dans tout l'Arrageois." },
  'Lens': { cp: '62300', intro: "Au cœur du bassin minier, Lens et son agglomération bénéficient d'une équipe d'Easers de proximité pour le ménage, le repassage et l'entretien des jardins." },
  'Liévin': { cp: '62800', intro: "À quelques minutes de notre agence de Servins, Liévin est l'une des communes que nous desservons le plus fréquemment : réactivité et Easer dédié garantis." },
  'Béthune': { cp: '62400', intro: "Béthune et l'agglomération Béthune-Bruay font partie intégrante de notre zone d'intervention. Ménage régulier, grand nettoyage ponctuel ou jardinage : tout est possible." },
  'Bruay-la-Buissière': { cp: '62700', intro: "Nos Easers accompagnent les familles et les seniors de Bruay-la-Buissière au quotidien, avec la même bienveillance et le même professionnalisme." },
  'Hénin-Beaumont': { cp: '62110', intro: "Hénin-Beaumont et le secteur Hénin-Carvin sont desservis par notre agence Artois. Profitez du crédit d'impôt 50% avec avance immédiate dès la première intervention." },
  'Carvin': { cp: '62220', intro: "À Carvin, nous facilitons la vie des actifs pressés et des retraités souhaitant rester chez eux dans les meilleures conditions." },
  'Douai': { cp: '59500', intro: "Douai et sa proche périphérie (Cuincy, Lambres-lez-Douai) marquent la limite est de notre zone d'intervention de 40 km autour de Servins." },
  'Saint-Pol-sur-Ternoise': { cp: '62130', intro: "Dans le Ternois, nos Easers se déplacent jusqu'à Saint-Pol-sur-Ternoise et ses environs pour le ménage, le repassage et l'entretien des espaces verts." },
  'Avion': { cp: '62210', intro: "Avion, voisine directe de Lens et Liévin, profite de créneaux d'intervention très flexibles grâce à la proximité de notre agence." },
  'Nœux-les-Mines': { cp: '62290', intro: "Nœux-les-Mines est au centre de notre zone : interventions rapides, Easer attitré et suivi qualité régulier." },
  'Aubigny-en-Artois': { cp: '62690', intro: "Aubigny-en-Artois et les villages alentour bénéficient d'une équipe locale qui connaît parfaitement le secteur rural de l'Artois." },
  'Bully-les-Mines': { cp: '62160', intro: "Bully-les-Mines se trouve à moins de 10 minutes de notre agence de Servins : idéal pour des interventions régulières avec un Easer dédié." },
  'Servins': { cp: '62530', intro: "Servins accueille le siège de notre agence Artois. C'est ici que tout a commencé : proximité maximale, réactivité et connaissance parfaite du secteur." },
  'Auchel': { cp: '62260', intro: "À Auchel et dans les communes voisines, nos Easers prennent en charge ménage, repassage, vitres et entretien des jardins." },
  'Lillers': { cp: '62190', intro: "Lillers marque la limite nord-ouest de notre zone d'intervention. Nous y assurons des prestations régulières et ponctuelles." },
  'Harnes': { cp: '62440', intro: "Harnes et ses environs (Noyelles-sous-Lens, Billy-Montigny) sont desservis avec la même exigence de qualité et de ponctualité." },
  'Wingles': { cp: '62410', intro: "À Wingles, nous accompagnons familles et seniors avec un Easer attitré qui connaît vos habitudes." },
};

const PRIORITY_CITIES = ['Arras', 'Lens', 'Liévin', 'Béthune', 'Bruay-la-Buissière', 'Hénin-Beaumont', 'Carvin', 'Douai', 'Avion', 'Nœux-les-Mines', 'Saint-Pol-sur-Ternoise', 'Aubigny-en-Artois'];

const agency = AGENCIES['artois.ptitservices'];

export const CITIES = agency.zones.flatMap((zone) =>
  zone.cities.map((name) => {
    const meta = CITY_META[name] || {};
    return {
      name,
      slug: slugify(name),
      region: zone.region,
      cp: meta.cp || '',
      intro: meta.intro || `${name} fait partie des communes desservies par notre agence Artois, située à Servins. Nos Easers s'y déplacent pour le ménage, le repassage, le nettoyage des vitres, le petit bricolage et l'entretien du jardin, avec le crédit d'impôt 50% en avance immédiate.`,
      priority: PRIORITY_CITIES.includes(name),
      agencyId: agency.id,
    };
  })
);

export const CITIES_BY_SLUG = Object.fromEntries(CITIES.map((c) => [c.slug, c]));

// ---- Dynamic communes (towns & villages added from the public search, served within 30 km of Servins) ----
export const SEARCH_RADIUS_KM = 30;
export const DYNAMIC_REGION = 'Villages et communes autour de Servins';
const DYNAMIC_BY_SLUG = {};
let dynamicLoaded = false;
let dynamicPromise = null;

const toCity = (d) => ({
  name: d.name,
  slug: d.slug,
  region: d.region || DYNAMIC_REGION,
  cp: d.cp || '',
  intro: `${d.name}${d.cp ? ` (${d.cp})` : ''} se situe à ${d.distance_km ? `${String(d.distance_km).replace('.', ',')} km` : 'moins de 30 km'} de notre agence de Servins : village ou bourg, nos Easers s'y déplacent pour le ménage, le repassage, les vitres, le jardinage, la taille de haies et le petit bricolage, avec le crédit d'impôt de 50 % en avance immédiate.`,
  priority: false,
  dynamic: true,
  distanceKm: d.distance_km,
  agencyId: agency.id,
});

export const registerDynamicCities = (list = []) => {
  list.forEach((d) => { if (d?.slug && !CITIES_BY_SLUG[d.slug]) DYNAMIC_BY_SLUG[d.slug] = toCity(d); });
  dynamicLoaded = true;
};

/** Fetch (once) the communes added through the search; safe to call many times. */
export const fetchDynamicCities = () => {
  if (dynamicLoaded) return Promise.resolve(Object.values(DYNAMIC_BY_SLUG));
  if (!dynamicPromise) {
    const base = process.env.REACT_APP_BACKEND_URL || '';
    dynamicPromise = fetch(`${base}/api/communes/dynamic`)
      .then((r) => (r.ok ? r.json() : { items: [] }))
      .then((data) => { registerDynamicCities(data.items || []); return Object.values(DYNAMIC_BY_SLUG); })
      .catch(() => { dynamicLoaded = true; return []; });
  }
  return dynamicPromise;
};

export const isDynamicLoaded = () => dynamicLoaded;
export const getDynamicCities = () => Object.values(DYNAMIC_BY_SLUG).sort((a, b) => a.name.localeCompare(b.name, 'fr'));
export const getCity = (slug) => CITIES_BY_SLUG[slug] || DYNAMIC_BY_SLUG[slug];
export const getMainCities = () => CITIES.filter((c) => c.priority);
export const getNearbyCities = (city, limit = 8) => {
  const sameRegion = CITIES.filter((c) => c.region === city.region && c.slug !== city.slug);
  // Villages added dynamically: point to the main towns of the area
  return (sameRegion.length ? sameRegion : getMainCities()).slice(0, limit);
};
export const getZones = () =>
  agency.zones.map((z) => ({ region: z.region, cities: z.cities.map((n) => CITIES_BY_SLUG[slugify(n)]) }));

export const cityPath = (city) => `/services-a-domicile/${city.slug}`;
